function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BasicModal, DebouncedModal, InfoModal, LoadingOverlay, LoadingSpinner } from '@deephaven/components';
import { ObjectFetcherContext, ObjectFetchManagerContext, sanitizeVariableDescriptor, useApi, useClient, WorkerVariablesContext } from '@deephaven/jsapi-bootstrap';
import Log from '@deephaven/log';
import { createWorkerVariablesStore } from '@deephaven/jsapi-utils';
import { assertNotNull } from '@deephaven/utils';
import { vsDebugDisconnect } from '@deephaven/icons';
import ConnectionContext from "./ConnectionContext.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
var log = Log.module('@deephaven/app-utils.ConnectionBootstrap');
/**
 * ConnectionBootstrap component. Handles initializing the connection.
 */
export function ConnectionBootstrap(_ref) {
  var children = _ref.children;
  var api = useApi();
  var client = useClient();
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    error = _useState2[0],
    setError = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    connection = _useState4[0],
    setConnection = _useState4[1];
  var _useState5 = useState('connecting'),
    _useState6 = _slicedToArray(_useState5, 2),
    connectionState = _useState6[0],
    setConnectionState = _useState6[1];
  var isAuthFailed = connectionState === 'failed';
  var isShutdown = connectionState === 'shutdown';
  var isReconnecting = connectionState === 'reconnecting';
  var isNotConnecting = connectionState === 'not_connecting';
  useEffect(function initConnection() {
    var isCanceled = false;
    function loadConnection() {
      return _loadConnection.apply(this, arguments);
    }
    function _loadConnection() {
      _loadConnection = _asyncToGenerator(function* () {
        try {
          var newConnection = yield client.getAsIdeConnection();
          if (isCanceled) {
            return;
          }
          setConnection(newConnection);
          setConnectionState('connected');
        } catch (e) {
          if (isCanceled) {
            return;
          }
          setError(e);
          setConnectionState('not_connecting');
        }
      });
      return _loadConnection.apply(this, arguments);
    }
    loadConnection();
    return () => {
      isCanceled = true;
    };
  }, [api, client]);
  useEffect(function listenForDisconnect() {
    if (connection == null || isShutdown) return;

    // handles the disconnect event
    function handleDisconnect(event) {
      var detail = event.detail;
      log.info('Disconnect', "".concat(JSON.stringify(detail)));
      setConnectionState('reconnecting');
    }
    var removerFn = connection.addEventListener(api.IdeConnection.EVENT_DISCONNECT, handleDisconnect);
    return removerFn;
  }, [api, connection, isShutdown]);
  useEffect(function listenForReconnect() {
    if (connection == null || isShutdown) return;

    // handles the reconnect event
    function handleReconnect(event) {
      var detail = event.detail;
      log.info('Reconnect', "".concat(JSON.stringify(detail)));
      setConnectionState('connected');
    }
    var removerFn = connection.addEventListener(api.CoreClient.EVENT_RECONNECT, handleReconnect);
    return removerFn;
  }, [api, connection, isShutdown]);
  useEffect(function listenForShutdown() {
    if (connection == null) return;

    // handles the shutdown event
    function handleShutdown(event) {
      var detail = event.detail;
      log.info('Shutdown', "".concat(JSON.stringify(detail)));
      setError("Server shutdown: ".concat(detail !== null && detail !== void 0 ? detail : 'Unknown reason'));
      setConnectionState('shutdown');
    }
    var removerFn = connection.addEventListener(api.IdeConnection.EVENT_SHUTDOWN, handleShutdown);
    return removerFn;
  }, [api, connection]);
  useEffect(function listenForAuthFailed() {
    if (connection == null || isShutdown) return;

    // handles the auth failed event
    function handleAuthFailed(event) {
      var detail = event.detail;
      log.warn('Reconnect authentication failed', "".concat(JSON.stringify(detail)));
      setError("Reconnect authentication failed: ".concat(detail !== null && detail !== void 0 ? detail : 'Unknown reason'));
      setConnectionState('failed');
    }
    var removerFn = connection.addEventListener(api.CoreClient.EVENT_RECONNECT_AUTH_FAILED, handleAuthFailed);
    return removerFn;
  }, [api, connection, isShutdown]);
  var objectFetcher = useCallback(/*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(function* (descriptor) {
      assertNotNull(connection, 'No connection available to fetch object with');
      if (typeof descriptor === 'string') {
        throw new Error('No URI resolvers available in Core');
      }
      return connection.getObject(sanitizeVariableDescriptor(descriptor));
    });
    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }(), [connection]);

  // Push-based worker variable store for DHC. Core exposes a single
  // connection, so the resolver ignores the worker key.
  var _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    workerVariablesStore = _useState8[0],
    setWorkerVariablesStore = _useState8[1];
  useEffect(function manageWorkerVariablesStore() {
    if (connection == null) {
      setWorkerVariablesStore(null);
      return undefined;
    }
    var store = createWorkerVariablesStore(/*#__PURE__*/_asyncToGenerator(function* () {
      return connection;
    }));
    setWorkerVariablesStore(store);
    return () => {
      store.destroy();
    };
  }, [connection]);

  /** We don't really need to do anything fancy in Core to manage an object, just fetch it  */
  var objectManager = useMemo(() => ({
    subscribe: (descriptor, onUpdate) => {
      // We send an update with the fetch right away
      onUpdate({
        fetch: () => objectFetcher(descriptor),
        status: 'ready'
      });
      return () => {
        // no-op
        // For Core, if the server dies then we can't reconnect anyway, so no need to bother listening for subscription or cleaning up
      };
    }
  }), [objectFetcher]);
  function handleRefresh() {
    log.info('Refreshing application');
    window.location.reload();
  }
  if (isShutdown || connectionState === 'connecting' || isNotConnecting) {
    return /*#__PURE__*/_jsx(LoadingOverlay, {
      "data-testid": "connection-bootstrap-loading",
      isLoading: error == null,
      errorMessage: error != null ? "".concat(error) : undefined
    });
  }
  return /*#__PURE__*/_jsx(ConnectionContext.Provider, {
    value: connection !== null && connection !== void 0 ? connection : null,
    children: /*#__PURE__*/_jsx(ObjectFetcherContext.Provider, {
      value: objectFetcher,
      children: /*#__PURE__*/_jsx(WorkerVariablesContext.Provider, {
        value: workerVariablesStore,
        children: /*#__PURE__*/_jsxs(ObjectFetchManagerContext.Provider, {
          value: objectManager,
          children: [children, /*#__PURE__*/_jsx(DebouncedModal, {
            isOpen: isReconnecting,
            debounceMs: 1000,
            children: /*#__PURE__*/_jsx(InfoModal, {
              icon: vsDebugDisconnect,
              title: /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx(LoadingSpinner, {}), " Attempting to reconnect..."]
              }),
              subtitle: "Please check your network connection."
            })
          }), /*#__PURE__*/_jsx(BasicModal, {
            confirmButtonText: "Refresh",
            onConfirm: handleRefresh,
            isOpen: isAuthFailed,
            headerText: "Authentication failed",
            bodyText: "Credentials are invalid. Please refresh your browser to try and reconnect."
          })]
        })
      })
    })
  });
}
export default ConnectionBootstrap;
//# sourceMappingURL=ConnectionBootstrap.js.map